import React, { useState, useEffect, useCallback } from 'react';
import { useAlert, useConfirm } from '../../components/ConfirmModal';
import { useSearchParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

// Moderation queue for reported community stories.
//
// Community stories publish without pre-moderation, so this queue is the
// "timely response" half of the App Store guideline 1.2 / Google Play UGC
// requirement. A story reported by enough distinct users is hidden
// automatically; this screen is where a human confirms or reverses that.

const REASON_LABELS = {
  inappropriate: 'Not appropriate for children',
  scary:         'Too scary / frightening',
  violent:       'Violent',
  sexual:        'Sexual content',
  hateful:       'Hateful or abusive',
  personal_info: 'Contains personal information',
  spam:          'Spam',
  other:         'Something else',
};

const REASON_TONE = {
  sexual: 'danger', hateful: 'danger', violent: 'danger',
  inappropriate: 'warning', personal_info: 'warning', scary: 'warning',
  spam: 'secondary', other: 'secondary',
};

const AdminReports = () => {
  const alert   = useAlert();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [byStory, setByStory] = useState([]);
  const [total,   setTotal]   = useState(0);
  const [pages,   setPages]   = useState(1);
  const [loading, setLoading] = useState(true);
  const [acting,  setActing]  = useState(null);

  const page   = parseInt(searchParams.get('page') || '1', 10);
  const status = searchParams.get('status') || 'pending';

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  const load = useCallback(() => {
    setLoading(true);
    API.get('/admin/community/reports', { params: { page, limit: 20, status } })
      .then(r => {
        setReports(r.data.reports || []);
        setByStory(r.data.byStory || []);
        setTotal(r.data.total || 0);
        setPages(r.data.pages || 1);
      })
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  // How many pending reports this story has — the signal for how urgent it is.
  const reportCount = (storyId) =>
    byStory.find(b => String(b._id) === String(storyId))?.count || 1;

  const act = async (report, action) => {
    if (action === 'hide') {
      const ok = await confirm({
        title: 'Hide this story?',
        message: 'It will be removed from the Community for everyone. All reports on it will be marked resolved.',
        confirmLabel: 'Hide story',
        variant: 'danger',
      });
      if (!ok) return;
    }
    setActing(report._id);
    try {
      await API.patch(`/admin/community/reports/${report._id}`, { action });
      load();
    } catch (err) {
      await alert({
        title: 'Could not update',
        message: err.response?.data?.message || 'Please try again.',
        danger: true,
      });
    } finally {
      setActing(null);
    }
  };

  return (
    <AdminLayout title="Reported Stories">
      <div className="d-flex flex-wrap gap-2 mb-3">
        {['pending', 'actioned', 'dismissed'].map(s => (
          <button key={s}
            className={`btn btn-sm rounded-pill px-3 text-capitalize ${status === s ? 'btn-dream' : 'btn-outline-secondary'}`}
            onClick={() => set('status', s)}>
            {s}
          </button>
        ))}
        <span className="ms-auto align-self-center small text-muted">
          {total} {total === 1 ? 'report' : 'reports'}
        </span>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" role="status" /></div>
      ) : reports.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-shield-check fs-1" style={{ color: 'var(--text-green)' }}></i>
            <p className="mt-2 mb-0" style={{ color: 'var(--text-muted2)' }}>
              No {status} reports. The Community is clear.
            </p>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {reports.map(r => {
            const count  = reportCount(r.story?._id);
            const hidden = r.story?.isHidden;
            return (
              <div key={r._id} className="card border-0 shadow-sm" style={{ borderRadius: '14px' }}>
                <div className="card-body">
                  <div className="d-flex flex-wrap align-items-start gap-2 mb-2">
                    <span className={`badge bg-${REASON_TONE[r.reason] || 'secondary'}`}>
                      {REASON_LABELS[r.reason] || r.reason}
                    </span>
                    {count > 1 && (
                      <span className="badge bg-danger">{count} reports</span>
                    )}
                    {hidden && <span className="badge bg-dark"><i className="bi bi-eye-slash me-1"></i>Hidden</span>}
                    <span className="ms-auto small" style={{ color: 'var(--text-soft)' }}>
                      {new Date(r.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>
                    {r.story?.publishSlug ? (
                      <Link to={`/community/${r.story.publishSlug}`} target="_blank" rel="noopener noreferrer"
                        className="text-decoration-none">
                        {r.story?.title || 'Untitled story'} <i className="bi bi-box-arrow-up-right small"></i>
                      </Link>
                    ) : (r.story?.title || 'Story removed')}
                  </h6>

                  <div className="small mb-2" style={{ color: 'var(--text-muted2)' }}>
                    Author: {r.storyOwner?.name || 'Unknown'}{r.storyOwner?.email ? ` (${r.storyOwner.email})` : ''}
                    {' · '}Reported by: {r.reporter?.name || 'Unknown'}
                  </div>

                  {r.details && (
                    <blockquote className="small p-2 mb-2 rounded"
                      style={{ background: 'var(--item-bg)', color: 'var(--text-body)' }}>
                      “{r.details}”
                    </blockquote>
                  )}

                  {r.status === 'pending' ? (
                    <div className="d-flex flex-wrap gap-2">
                      {!hidden && (
                        <button className="btn btn-sm btn-danger rounded-pill px-3"
                          onClick={() => act(r, 'hide')} disabled={acting === r._id}>
                          <i className="bi bi-eye-slash me-1"></i>Hide story
                        </button>
                      )}
                      {hidden && (
                        <button className="btn btn-sm btn-outline-success rounded-pill px-3"
                          onClick={() => act(r, 'restore')} disabled={acting === r._id}>
                          <i className="bi bi-arrow-counterclockwise me-1"></i>Restore story
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                        onClick={() => act(r, 'dismiss')} disabled={acting === r._id}>
                        Dismiss report
                      </button>
                    </div>
                  ) : (
                    <span className="small text-capitalize" style={{ color: 'var(--text-soft)' }}>
                      {r.status}{r.reviewedAt ? ` · ${new Date(r.reviewedAt).toLocaleDateString()}` : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-4">
          <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
            disabled={page <= 1} onClick={() => set('page', String(page - 1))}>Previous</button>
          <span className="align-self-center small" style={{ color: 'var(--text-muted2)' }}>
            Page {page} of {pages}
          </span>
          <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
            disabled={page >= pages} onClick={() => set('page', String(page + 1))}>Next</button>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReports;
